import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { ThemeBlueprint } from '@backstage/plugin-app-react';
import {
  UnifiedThemeProvider,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';
import DarkModeIcon from '@material-ui/icons/Brightness2';
import LightModeIcon from '@material-ui/icons/WbSunny';

/**
 * Traffic Solvers themes.
 *
 * The palette is the portal's, so the two products do not look like they came
 * from different companies. Values are taken from the portal's design tokens in
 * `src/styles/globals.css` (converted from HSL) and from the logo itself.
 *
 * These extensions are named `light` and `dark` on purpose: those are the ids of
 * the app's built-in themes, so declaring them here replaces them rather than
 * adding a third and fourth option to user settings.
 */

// Straight off the logo.
const BRAND_BLUE = '#3868E8'; // rgb(56,104,232) — also the portal's --primary
const BRAND_ORANGE = '#FC7B13'; // rgb(252,123,19)
const BRAND_NAVY = '#1D1B38'; // rgb(29,27,56) — the wordmark's dark

// Portal semantic tokens.
const SUCCESS = '#16A260';
const WARNING = '#F59F0A';
const ERROR = '#EF4444';
const INFO = '#006EFF';

const LIGHT_BG = '#F6F7F9';
const LIGHT_PAPER = '#FFFFFF';
const LIGHT_TEXT = '#020817';

const DARK_BG = '#000000';
const DARK_PAPER = '#15181B'; // the portal's dark --card
const DARK_TEXT = '#F7FAFC';

// Page headers. The blue-to-navy sweep is the portal's header treatment; the
// orange one marks the places you go to change something rather than read it.
const brandPages = (from: string, to: string) => ({
  home: genPageTheme({ colors: [from, to], shape: shapes.wave }),
  documentation: genPageTheme({ colors: [from, to], shape: shapes.wave2 }),
  tool: genPageTheme({
    colors: [BRAND_ORANGE, '#D65F00'],
    shape: shapes.round,
  }),
  service: genPageTheme({ colors: [from, to], shape: shapes.wave }),
  website: genPageTheme({ colors: [from, to], shape: shapes.wave }),
  library: genPageTheme({ colors: [from, to], shape: shapes.wave }),
  other: genPageTheme({ colors: [from, to], shape: shapes.wave }),
  app: genPageTheme({ colors: [from, to], shape: shapes.wave }),
  apis: genPageTheme({ colors: [from, to], shape: shapes.wave2 }),
});

const trafficSolversLight = createUnifiedTheme({
  fontFamily: `Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`,
  palette: {
    ...palettes.light,
    primary: { main: BRAND_BLUE, dark: '#2B51B8', light: '#6B8FEF' },
    secondary: { main: BRAND_ORANGE, dark: '#D65F00', light: '#FF9A47' },
    error: { main: ERROR },
    warning: { main: WARNING },
    success: { main: SUCCESS },
    info: { main: INFO },
    background: { default: LIGHT_BG, paper: LIGHT_PAPER },
    text: { primary: LIGHT_TEXT, secondary: '#5A6472' },
    // The sidebar stays navy in both themes — it carries the white wordmark.
    navigation: {
      background: BRAND_NAVY,
      indicator: BRAND_ORANGE,
      color: '#D5D6E0',
      selectedColor: '#FFFFFF',
      navItem: { hoverBackground: '#2A2850' },
    },
  },
  defaultPageTheme: 'home',
  pageTheme: brandPages(BRAND_BLUE, BRAND_NAVY),
});

const trafficSolversDark = createUnifiedTheme({
  fontFamily: `Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`,
  palette: {
    ...palettes.dark,
    primary: { main: '#6B8FEF', dark: BRAND_BLUE, light: '#9DB4F5' },
    secondary: { main: BRAND_ORANGE, dark: '#D65F00', light: '#FF9A47' },
    error: { main: ERROR },
    warning: { main: WARNING },
    success: { main: SUCCESS },
    info: { main: INFO },
    background: { default: DARK_BG, paper: DARK_PAPER },
    text: { primary: DARK_TEXT, secondary: '#A0AEC0' },
    navigation: {
      background: BRAND_NAVY,
      indicator: BRAND_ORANGE,
      color: '#D5D6E0',
      selectedColor: '#FFFFFF',
      navItem: { hoverBackground: '#2A2850' },
    },
  },
  defaultPageTheme: 'home',
  pageTheme: brandPages('#2B51B8', '#0E0D1C'),
});

const lightTheme = ThemeBlueprint.make({
  name: 'light',
  params: {
    theme: {
      id: 'light',
      title: 'Traffic Solvers Light',
      variant: 'light',
      icon: <LightModeIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={trafficSolversLight}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  },
});

const darkTheme = ThemeBlueprint.make({
  name: 'dark',
  params: {
    theme: {
      id: 'dark',
      title: 'Traffic Solvers Dark',
      variant: 'dark',
      icon: <DarkModeIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={trafficSolversDark}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  },
});

export const themeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [lightTheme, darkTheme],
});
